import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { API_BASE } from '../api/starviewApi';

// Real accounts, shared with the mobile app - the website signs in against
// the exact same auth-service (signup/signin) the app already uses, so a
// ticket bought on the website shows up in the app's own account and vice
// versa. Token is kept in localStorage (the website's equivalent of the
// app's SecureStore) under the same purpose, not the same storage mechanism.
const TOKEN_KEY = 'starview_web_token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(options.headers || {}),
        },
      });
      if (res.status === 401) {
        setToken(null);
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
      }
      return res;
    },
    [token]
  );

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    authFetch(`${API_BASE}/api/api/v1/auth/currentuser`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.user) setUser(data.user);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = useCallback(async (username, password) => {
    const res = await fetch(`${API_BASE}/api/api/v1/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to sign in');
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user || null);
    return data;
  }, []);

  const signup = useCallback(async ({ username, email, password, country }) => {
    const res = await fetch(`${API_BASE}/api/api/v1/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password, country }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to sign up');
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user || null);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, loading, isAuthenticated: !!token, authFetch, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
